import {
    useParamsHistory,
    useUrlParams,
    QueryParamsHandler,
    paramParsers,
    useQueryParams,
} from '@servicetitan/cp-react-hooks';

type Keys = 'invite' | 'login' | 'loginOnInvite' | 'register' | 'recovery' | 'recoveryCode' | 'mfa';

type LoginInfoType = 'exists' | 'ok';
const loginInfoTypes: LoginInfoType[] = ['exists', 'ok'];

type RegisterInfoType = 'invite_expired';
const registerInfoTypes: RegisterInfoType[] = ['invite_expired'];

type RecoveryInfoType = 'expired';
const recoveryInfoTypes: RecoveryInfoType[] = ['expired'];

interface AuthUrlParams {
    loginOnInvite: { code?: string };
    invite: { code: string };
    recoveryCode: { code: string };
    mfa: {};
}

interface AuthQueryParams {
    login: { redirect?: string; info?: LoginInfoType };
    loginOnInvite: { redirect?: string; info?: LoginInfoType };
    register: { redirect?: string; info: RegisterInfoType };
    recovery: { info?: RecoveryInfoType };
    invite: { auto?: boolean };
    mfa: { keepSigned?: string };
}

const AuthUrlMap: Record<Keys, string> = {
    invite: '/register/:code',
    login: '/login',
    loginOnInvite: '/login/:code',
    register: '/register',
    recovery: '/recovery',
    recoveryCode: '/recovery/:code',
    mfa: '/mfa',
};

const loginQueryHandler = QueryParamsHandler.from<AuthQueryParams['login']>({
    redirect: QueryParamsHandler.parserString,
    info: QueryParamsHandler.querySingleParam(
        paramParsers.stringSet<LoginInfoType>(loginInfoTypes)
    ),
});

const registerQueryHandler = QueryParamsHandler.from<AuthQueryParams['register']>({
    redirect: QueryParamsHandler.parserString,
    info: QueryParamsHandler.querySingleParam(
        paramParsers.stringSet<RegisterInfoType>(registerInfoTypes)
    ),
});

const recoveryQueryHandler = QueryParamsHandler.from<AuthQueryParams['recovery']>({
    info: QueryParamsHandler.querySingleParam(
        paramParsers.stringSet<RecoveryInfoType>(recoveryInfoTypes)
    ),
});

const inviteQueryHandler = QueryParamsHandler.from<AuthQueryParams['invite']>({
    auto: QueryParamsHandler.parserBoolean,
});

const mfaQueryHandler = QueryParamsHandler.from<AuthQueryParams['mfa']>({
    keepSigned: QueryParamsHandler.parserString,
});

export const useAuthHistory = () =>
    useParamsHistory<Keys, AuthUrlParams, AuthQueryParams>(AuthUrlMap, {
        nestedPath: '/auth',
        queryHandlers: {
            login: loginQueryHandler,
            loginOnInvite: loginQueryHandler,
            invite: inviteQueryHandler,
            register: registerQueryHandler,
            recovery: recoveryQueryHandler,
            mfa: mfaQueryHandler,
        },
    });

export const useAuthCodeParams = () => useUrlParams<AuthUrlParams['invite']>();
export const useAuthLoginQueryParams = () => useQueryParams(loginQueryHandler);
export const useAuthInviteQueryParams = () => useQueryParams(inviteQueryHandler);
export const useAuthRegisterQueryParams = () => useQueryParams(registerQueryHandler);
export const useAuthRecoveryQueryParams = () => useQueryParams(recoveryQueryHandler);
export const useAuthMfaQueryParams = () => useQueryParams(mfaQueryHandler);
